import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ChevronUpIcon, ChevronDownIcon } from 'lucide-react';
interface MonthYearPickerProps {
  currentYear: number;
  currentMonth: number; // 0-11
  onSelect: (year: number, month: number) => void;
  onClose: () => void;
  isDark: boolean;
  isMobile?: boolean;
  triggerRef?: React.RefObject<HTMLButtonElement>;
}
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export const MonthYearPicker: React.FC<MonthYearPickerProps> = ({
  currentYear,
  currentMonth,
  onSelect,
  onClose,
  isDark,
  isMobile = false,
  triggerRef
}) => {
  console.log('🔴 [PICKER COMPONENT] MonthYearPicker MOUNTED/RENDERED', {
    currentYear,
    currentMonth,
    isMobile,
    timestamp: new Date().toISOString()
  });
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [position, setPosition] = useState({
    top: 0,
    left: 0
  });
  const pickerRef = useRef<HTMLDivElement>(null);
  const todayMonth = new Date().getMonth();
  const todayYear = new Date().getFullYear();
  // Calculate position based on trigger button (desktop only)
  useEffect(() => {
    if (!isMobile && triggerRef?.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const pickerWidth = 288; // w-72 = 288px
      const pickerHeight = 400; // estimated height
      // Position below trigger, centered
      let left = triggerRect.left + triggerRect.width / 2 - pickerWidth / 2;
      let top = triggerRect.bottom + 8;
      // Clamp to viewport bounds
      const padding = 16;
      left = Math.max(padding, Math.min(left, window.innerWidth - pickerWidth - padding));
      top = Math.max(padding, Math.min(top, window.innerHeight - pickerHeight - padding));
      setPosition({
        top,
        left
      });
      console.log('🔴 [PICKER COMPONENT] Position calculated:', {
        top,
        left,
        triggerRect
      });
    }
  }, [isMobile, triggerRef]);
  // Handle ESC key
  useEffect(() => {
    console.log('🔴 [PICKER COMPONENT] ESC handler registered');
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        console.log('🔴 [PICKER COMPONENT] ESC key pressed - closing picker');
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => {
      console.log('🔴 [PICKER COMPONENT] ESC handler cleanup');
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);
  // Handle click outside - with trigger button exclusion
  useEffect(() => {
    console.log('🔴 [PICKER COMPONENT] Click-outside handler registered');
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      // Exclude trigger button from outside-click detection
      if (triggerRef?.current?.contains(target)) {
        console.log('🔴 [PICKER COMPONENT] Click on trigger button - ignoring');
        return;
      }
      if (pickerRef.current && !pickerRef.current.contains(target)) {
        console.log('🔴 [PICKER COMPONENT] Click outside detected - closing picker');
        onClose();
      }
    };
    // Increased delay to prevent immediate closure
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
      console.log('🔴 [PICKER COMPONENT] Click-outside listener active (after 200ms delay)');
    }, 200);
    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
      console.log('🔴 [PICKER COMPONENT] Click-outside handler cleanup');
    };
  }, [onClose, triggerRef]);
  const handleYearChange = (delta: number) => {
    const newYear = selectedYear + delta;
    console.log('🔴 [PICKER COMPONENT] Year button clicked:', {
      delta,
      oldYear: selectedYear,
      newYear
    });
    setSelectedYear(newYear);
  };
  const handleMonthSelect = (monthIndex: number) => {
    console.log('🔴 [PICKER COMPONENT] Month button clicked:', {
      year: selectedYear,
      monthIndex,
      monthName: MONTHS[monthIndex]
    });
    console.log('🔴 [PICKER COMPONENT] Calling onSelect callback...');
    onSelect(selectedYear, monthIndex);
    console.log('🔴 [PICKER COMPONENT] onSelect callback completed');
  };
  const isCurrentMonth = (monthIndex: number) => {
    return monthIndex === currentMonth && selectedYear === currentYear;
  };
  const isTodayMonth = (monthIndex: number) => {
    return monthIndex === todayMonth && selectedYear === todayYear;
  };
  const pickerContent = isMobile ?
  // Mobile: Bottom sheet
  <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 z-[9998] animate-in fade-in duration-200" onClick={onClose} data-id="element-678" />
      {/* Bottom Sheet */}
      <div ref={pickerRef} className={`fixed bottom-0 left-0 right-0 z-[9999] rounded-t-2xl ${isDark ? 'bg-navy-800 border-t border-navy-600' : 'bg-white border-t border-gray-200'} shadow-2xl animate-in slide-in-from-bottom duration-300`} role="dialog" aria-label="Select month and year" data-id="element-679">
        {/* Drag handle */}
        <div className="flex justify-center pt-3 pb-2" data-id="element-680">
          <div className={`w-12 h-1 rounded-full ${isDark ? 'bg-navy-600' : 'bg-gray-300'}`} data-id="element-681" />
        </div>

        <div className="p-6 pb-8" data-id="element-682">
          {/* Year selector */}
          <div className="flex items-center justify-between mb-6" data-id="element-683">
            <button onClick={e => {
            e.stopPropagation();
            handleYearChange(-1);
          }} className={`p-3 rounded-lg ${isDark ? 'hover:bg-navy-700 text-white' : 'hover:bg-gray-100 text-gray-800'} transition-colors`} aria-label="Previous year" data-id="element-684">
              <ChevronUpIcon className="h-5 w-5" data-id="element-685" />
            </button>
            <span className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-686">
              {selectedYear}
            </span>
            <button onClick={e => {
            e.stopPropagation();
            handleYearChange(1);
          }} className={`p-3 rounded-lg ${isDark ? 'hover:bg-navy-700 text-white' : 'hover:bg-gray-100 text-gray-800'} transition-colors`} aria-label="Next year" data-id="element-687">
              <ChevronDownIcon className="h-5 w-5" data-id="element-688" />
            </button>
          </div>

          {/* Month grid */}
          <div className="grid grid-cols-3 gap-3" data-id="element-689">
            {MONTHS.map((month, index) => {
            const isSelected = isCurrentMonth(index);
            const isToday = isTodayMonth(index);
            return <button key={month} onClick={e => {
              e.stopPropagation();
              handleMonthSelect(index);
            }} className={`relative py-4 px-3 rounded-lg text-sm font-medium transition-all ${isSelected ? 'bg-transparent text-[#10B981] border-b-2 border-[#10B981]' : isToday ? isDark ? 'bg-deep-pink/20 text-deep-pink border-2 border-deep-pink' : 'bg-deep-pink/10 text-deep-pink border-2 border-deep-pink' : isDark ? 'bg-navy-700 text-white hover:bg-navy-600' : 'bg-gray-100 text-gray-800 hover:bg-gray-200'}`} aria-label={`${month} ${selectedYear}`} aria-current={isToday ? 'date' : undefined} data-id="element-690">
                  {month}
                </button>;
          })}
          </div>
        </div>
      </div>
    </> :
  // Desktop: Fixed positioned popover with portal
  <div ref={pickerRef} className={`fixed w-72 rounded-xl ${isDark ? 'bg-navy-800 border border-navy-600' : 'bg-white border border-gray-200'} shadow-2xl z-[9999] animate-in fade-in zoom-in-95 duration-200`} style={{
    top: `${position.top}px`,
    left: `${position.left}px`
  }} role="dialog" aria-label="Select month and year" data-id="element-691">
      <div className="p-4" data-id="element-692">
        {/* Year selector */}
        <div className="flex items-center justify-between mb-4" data-id="element-693">
          <button onClick={e => {
          e.stopPropagation();
          handleYearChange(-1);
        }} className={`p-2 rounded-lg ${isDark ? 'hover:bg-navy-700 text-white' : 'hover:bg-gray-100 text-gray-800'} transition-colors`} aria-label="Previous year" data-id="element-694">
            <ChevronUpIcon className="h-4 w-4" data-id="element-695" />
          </button>
          <span className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-696">
            {selectedYear}
          </span>
          <button onClick={e => {
          e.stopPropagation();
          handleYearChange(1);
        }} className={`p-2 rounded-lg ${isDark ? 'hover:bg-navy-700 text-white' : 'hover:bg-gray-100 text-gray-800'} transition-colors`} aria-label="Next year" data-id="element-697">
            <ChevronDownIcon className="h-4 w-4" data-id="element-698" />
          </button>
        </div>

        {/* Month grid */}
        <div className="grid grid-cols-3 gap-2" data-id="element-699">
          {MONTHS.map((month, index) => {
          const isSelected = isCurrentMonth(index);
          const isToday = isTodayMonth(index);
          return <button key={month} onClick={e => {
            e.stopPropagation();
            handleMonthSelect(index);
          }} className={`relative py-2.5 px-3 rounded-lg text-sm font-medium transition-all ${isSelected ? 'bg-transparent text-[#10B981] border-b-2 border-[#10B981]' : isToday ? isDark ? 'bg-deep-pink/20 text-deep-pink border-2 border-deep-pink' : 'bg-deep-pink/10 text-deep-pink border-2 border-deep-pink' : isDark ? 'bg-navy-700 text-white hover:bg-navy-600' : 'bg-gray-100 text-gray-800 hover:bg-gray-200'}`} aria-label={`${month} ${selectedYear}`} aria-current={isToday ? 'date' : undefined} data-id="element-700">
                {month}
              </button>;
        })}
        </div>
      </div>
    </div>;
  // Render via portal to document.body
  return createPortal(pickerContent, document.body);
};